// E-Invoicing-Einstellungen (nur Admin): Verkaeufer-Stammdaten (eigene Firma) und
// Nummernkreis fuer von Hand erfasste Ausgangsrechnungen (SAAS-MODUS-DESIGN §4a).

import React, { useEffect, useState } from 'react';
import { Card, SectionTitle, inputCls, btnPrimary, btnGhost } from './ui';
import { getSeller, saveSeller, getNumberRange, saveNumberRange } from '../api';

const EMPTY = {
  name: '', vatId: '', taxNumber: '',
  electronicAddress: { scheme: 'EM', id: '' },
  address: { street: '', city: '', postCode: '', countryCode: 'DE' },
  contact: { name: '', email: '', phone: '' },
  paymentMeans: { iban: '', bic: '' },
};

// Wert an einem Punkt-Pfad ('address.city') lesen/unveraenderlich setzen.
const getIn = (o, path) => path.split('.').reduce((v, k) => (v ? v[k] : ''), o) ?? '';
const setIn = (o, [k, ...rest], v) => ({ ...o, [k]: rest.length ? setIn(o[k] || {}, rest, v) : v });

const SellerSettings = ({ user, isDarkMode, t, onClose, showSuccess, showError }) => {
  const [seller, setSeller] = useState(EMPTY);
  const [range, setRange] = useState({ prefix: 'RE-', nextNumber: 1 });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Server speichert normalisiert (alle Felder vorhanden) -> direkt uebernehmen.
    getSeller(user).then((r) => r.seller && setSeller(r.seller)).catch((e) => showError?.(e.message));
    getNumberRange(user).then(setRange).catch((e) => showError?.(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const labelCls = `block text-xs mb-1 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`;
  const field = (path, label, extra = {}) => (
    <label className="block min-w-0">
      <span className={labelCls}>{label}</span>
      <input className={`${inputCls(isDarkMode)} w-full`} value={getIn(seller, path)}
        onChange={(e) => setSeller((s) => setIn(s, path.split('.'), e.target.value))} {...extra} />
    </label>
  );

  const save = async () => {
    setBusy(true);
    try {
      const r = await saveSeller(user, seller);
      if (r.ok === false) { showError?.(r.error); return; }
      const n = await saveNumberRange(user, { prefix: range.prefix, nextNumber: Number(range.nextNumber) });
      if (n.ok === false) { showError?.(n.error); return; }
      showSuccess?.(t('einvoicing.settings.saved'));
      onClose();
    } catch (e) { showError?.(e.message); }
    finally { setBusy(false); }
  };

  const grid = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3';
  const sub = `text-xs font-semibold mt-5 mb-2 ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`;

  return (
    <Card isDarkMode={isDarkMode}>
      <SectionTitle isDarkMode={isDarkMode}>{t('einvoicing.settings.title')}</SectionTitle>
      <h4 className={sub}>{t('einvoicing.settings.seller')}</h4>
      <p className={`text-xs mb-3 ${isDarkMode ? 'text-white/45' : 'text-slate-500'}`}>{t('einvoicing.settings.sellerHint')}</p>
      <div className={grid}>
        {field('name', t('einvoicing.manual.buyerName'), { required: true })}
        {field('vatId', t('einvoicing.manual.vatId'))}
        {field('taxNumber', t('einvoicing.manual.taxNumber'))}
        {field('address.street', t('einvoicing.manual.street'), { required: true })}
        {field('address.postCode', t('einvoicing.manual.postCode'), { required: true })}
        {field('address.city', t('einvoicing.manual.city'), { required: true })}
        {field('address.countryCode', t('einvoicing.manual.country'), { maxLength: 2, required: true })}
        {field('electronicAddress.id', t('einvoicing.manual.eAddress'), { type: 'email', required: true })}
      </div>
      <h4 className={sub}>{t('einvoicing.settings.contact')}</h4>
      <div className={grid}>
        {field('contact.name', t('einvoicing.settings.contactName'), { required: true })}
        {field('contact.phone', t('einvoicing.settings.phone'), { required: true })}
        {field('contact.email', t('einvoicing.settings.email'), { type: 'email', required: true })}
      </div>
      <h4 className={sub}>{t('einvoicing.settings.bank')}</h4>
      <div className={grid}>
        {field('paymentMeans.iban', t('einvoicing.settings.iban'), { required: true })}
        {field('paymentMeans.bic', t('einvoicing.settings.bic'))}
      </div>
      <h4 className={sub}>{t('einvoicing.settings.numberRange')}</h4>
      <div className={grid}>
        <label className="block min-w-0">
          <span className={labelCls}>{t('einvoicing.settings.prefix')}</span>
          <input className={`${inputCls(isDarkMode)} w-full`} maxLength={20} value={range.prefix}
            onChange={(e) => setRange((r) => ({ ...r, prefix: e.target.value }))} />
        </label>
        <label className="block min-w-0">
          <span className={labelCls}>{t('einvoicing.settings.nextNumber')}</span>
          <input type="number" min={1} step={1} className={`${inputCls(isDarkMode)} w-full`} value={range.nextNumber}
            onChange={(e) => setRange((r) => ({ ...r, nextNumber: e.target.value }))} />
        </label>
      </div>
      <div className="flex gap-2 justify-end mt-5">
        <button className={btnGhost(isDarkMode)} onClick={onClose}>{t('common.cancel')}</button>
        <button className={btnPrimary(isDarkMode)} disabled={busy} onClick={save}>{t('common.save')}</button>
      </div>
    </Card>
  );
};

export default SellerSettings;
